import React, { useMemo, useState } from 'react';
import { ChevronDown, FileText, Mail, Search, ExternalLink, Layers } from 'lucide-react';
import { SourceOverlay } from './SourceOverlay';
type SourceType = 'email' | 'document';
interface SourceItem {
  id: string;
  type: SourceType;
  title: string;
  date: string;
  referencedIn: string[];
  extractionsCount: number;
  sampleExtraction: string;
  sampleContext: string;
  timestamp: number; // For sorting
}
// Helper to create rough timestamps for sorting
const now = new Date('2026-04-16T12:00:00Z').getTime(); // Using the prompt's current date context
const day = 24 * 60 * 60 * 1000;
const SOURCES_DATA: SourceItem[] = [
// EMAILS
{
  id: 'e1',
  type: 'email',
  title: 'Re: Weekly Status Update',
  date: 'Today, 9:00 AM',
  referencedIn: ['Overview'],
  extractionsCount: 1,
  sampleExtraction: 'Portfolio execution remains at an early-to-mid stage. PSA drafting is advancing...',
  sampleContext: 'Project Status Summary',
  timestamp: now
}, {
  id: 'e2',
  type: 'email',
  title: 'Re: Next Steps & Action Items',
  date: 'Today, 10:15 AM',
  referencedIn: ['Overview'],
  extractionsCount: 5,
  sampleExtraction: 'Obtain DDQ responses from GP counsel',
  sampleContext: 'Task Action Item',
  timestamp: now - 1000 // slightly older
}, {
  id: 'e3',
  type: 'email',
  title: 'FW: Milestone Confirmations',
  date: 'Yesterday, 4:30 PM',
  referencedIn: ['Milestones'],
  extractionsCount: 4,
  sampleExtraction: 'Proposed Final Closing Deadline per draft PSA',
  sampleContext: 'Milestone Description',
  timestamp: now - day
}, {
  id: 'e4',
  type: 'email',
  title: 'Re: EQT Infrastructure Transfer',
  date: 'Apr 9, 2026',
  referencedIn: ['Consents'],
  extractionsCount: 3,
  sampleExtraction: 'Await EQT DDQ responses and notify EQT of selected buyer.',
  sampleContext: 'Next Required Action',
  timestamp: new Date('2026-04-09').getTime()
}, {
  id: 'e5',
  type: 'email',
  title: 'Re: Brookfield DDQ Follow-up',
  date: 'Apr 7, 2026',
  referencedIn: ['Consents'],
  extractionsCount: 2,
  sampleExtraction: 'Akin to review partial DDQ responses',
  sampleContext: 'Next Required Action',
  timestamp: new Date('2026-04-07').getTime()
}, {
  id: 'e6',
  type: 'email',
  title: 'Re: InstarAGF ROFO Process',
  date: 'Apr 7, 2026',
  referencedIn: ['Consents'],
  extractionsCount: 2,
  sampleExtraction: 'Coordinate ROFO process timing',
  sampleContext: 'Next Required Action',
  timestamp: new Date('2026-04-07').getTime() - 1000
}, {
  id: 'e7',
  type: 'email',
  title: 'Re: TA Status Update',
  date: 'Recent',
  referencedIn: ['Consents'],
  extractionsCount: 12,
  sampleExtraction: 'GP approval expected imminently.',
  sampleContext: 'Transfer Agreement Status',
  timestamp: now - 2 * day
}, {
  id: 'e8',
  type: 'email',
  title: 'Latest Correspondence',
  date: 'Recent',
  referencedIn: ['Consents'],
  extractionsCount: 15,
  sampleExtraction: "24 Feb '26",
  sampleContext: 'Last Communication Date',
  timestamp: now - 3 * day
},
// DOCUMENTS
{
  id: 'd1',
  type: 'document',
  title: 'Project Timeline Schedule.pdf',
  date: 'Mar 12, 2026',
  referencedIn: ['Overview'],
  extractionsCount: 5,
  sampleExtraction: 'Apr 18, 2026',
  sampleContext: 'Task Deadline',
  timestamp: new Date('2026-03-12').getTime()
}, {
  id: 'd2',
  type: 'document',
  title: 'Draft Transfer Agreement v3.pdf',
  date: 'Mar 10, 2026',
  referencedIn: ['Milestones', 'Consents'],
  extractionsCount: 8,
  sampleExtraction: 'Oct 15, 2026',
  sampleContext: 'Milestone Date',
  timestamp: new Date('2026-03-10').getTime()
}, {
  id: 'd3',
  type: 'document',
  title: 'KKR DDQ Response Pack.pdf',
  date: 'Apr 7, 2026',
  referencedIn: ['Consents'],
  extractionsCount: 4,
  sampleExtraction: "Confirm final purchaser is not a '40 Act Vehicle",
  sampleContext: 'Next Required Action',
  timestamp: new Date('2026-04-07').getTime() + 1000
}, {
  id: 'd4',
  type: 'document',
  title: 'Brookfield Tax Section Draft.pdf',
  date: 'Apr 5, 2026',
  referencedIn: ['Consents'],
  extractionsCount: 2,
  sampleExtraction: 'Provide DDQ Tax section responses',
  sampleContext: 'Next Required Action',
  timestamp: new Date('2026-04-05').getTime()
}, {
  id: 'd5',
  type: 'document',
  title: 'Action Items Tracker.pdf',
  date: 'Recent',
  referencedIn: ['Consents'],
  extractionsCount: 10,
  sampleExtraction: 'Obtain GP confirmation of transfer approval.',
  sampleContext: 'Next Required Action',
  timestamp: now - 4 * day
}, {
  id: 'd6',
  type: 'document',
  title: 'Invoice Schedule.pdf',
  date: 'Recent',
  referencedIn: ['Consents'],
  extractionsCount: 12,
  sampleExtraction: '$10,000.00',
  sampleContext: 'Invoice Amount',
  timestamp: now - 5 * day
}, {
  id: 'd7',
  type: 'document',
  title: 'Transfer Agreement - Precedent.pdf',
  date: 'Oct 10, 2025',
  referencedIn: ['Documents'],
  extractionsCount: 0,
  sampleExtraction: 'Standard transfer terms apply.',
  sampleContext: 'Legal Template',
  timestamp: new Date('2025-10-10').getTime()
}];
export function DocumentsTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    emails: false,
    documents: false
  });
  // Overlay state
  const [selectedSource, setSelectedSource] = useState<SourceItem | null>(null);
  const toggleSection = (section: string) => {
    setCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const filteredSources = useMemo(() => {
    if (!searchQuery.trim()) return SOURCES_DATA;
    const query = searchQuery.toLowerCase();
    return SOURCES_DATA.filter(s => s.title.toLowerCase().includes(query) || s.referencedIn.some(r => r.toLowerCase().includes(query)));
  }, [searchQuery]);
  const emails = filteredSources.filter(s => s.type === 'email').sort((a, b) => b.timestamp - a.timestamp);
  const documents = filteredSources.filter(s => s.type === 'document').sort((a, b) => b.timestamp - a.timestamp);
  const renderSourceRow = (source: SourceItem) => {
    const isEmail = source.type === 'email';
    const Icon = isEmail ? Mail : FileText;
    const iconColor = isEmail ? 'text-teal-500' : 'text-blue-500';
    const iconBg = isEmail ? 'bg-teal-50' : 'bg-blue-50';
    const hoverBg = isEmail ? 'hover:bg-teal-50/30' : 'hover:bg-blue-50/30';
    return <div key={source.id} onClick={() => setSelectedSource(source)} className={`flex items-center justify-between py-4 px-6 border-b border-gray-100 cursor-pointer transition-colors ${hoverBg} group`} data-id="element-130">
        <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-131">
          <div className={`p-2 rounded-lg ${iconBg} ${iconColor} flex-shrink-0`} data-id="element-132">
            <Icon className="w-5 h-5" data-id="element-133" />
          </div>
          <div className="flex flex-col min-w-0" data-id="element-134">
            <span className="text-sm font-semibold text-gray-900 truncate group-hover:text-gray-700 transition-colors" data-id="element-135">
              {source.title}
            </span>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500" data-id="element-136">
              <span data-id="element-137">{source.date}</span>
              <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-138" />
              <div className="flex items-center gap-1" data-id="element-139">
                <span className="text-gray-400" data-id="element-140">Referenced in:</span>
                <div className="flex gap-1.5" data-id="element-141">
                  {source.referencedIn.map((ref, i) => <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium" data-id="element-142">
                      {ref}
                    </span>)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6 flex-shrink-0 ml-4" data-id="element-143">
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-md border border-gray-200" data-id="element-144">
            <Layers className="w-3.5 h-3.5 text-gray-400" data-id="element-145" />
            {source.extractionsCount}{' '}
            {source.extractionsCount === 1 ? 'extraction' : 'extractions'}
          </div>
          <button className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity hover:text-gray-600" data-id="element-146">
            <ExternalLink className="w-4 h-4" data-id="element-147" />
          </button>
        </div>
      </div>;
  };
  return <div className="animate-in fade-in duration-300 max-w-5xl mx-auto" data-id="element-148">
      {/* Header & Search */}
      <div className="flex items-center justify-between mb-8" data-id="element-149">
        <div data-id="element-150">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-151">Source Documents</h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-152">
            Browse all emails and documents referenced across this project.
          </p>
        </div>
        <div className="flex w-80 shadow-sm rounded-md overflow-hidden border border-gray-300 bg-white" data-id="element-153">
          <input type="text" placeholder="Search sources..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="flex-1 px-4 py-2 text-sm outline-none" data-id="element-154" />
          <div className="bg-gray-50 text-gray-400 px-3 py-2 border-l border-gray-200 flex items-center justify-center" data-id="element-155">
            <Search className="w-4 h-4" data-id="element-156" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-157">
        {/* Emails Section */}
        <div className="flex flex-col" data-id="element-158">
          <div className="flex items-center justify-between py-4 px-6 bg-gray-50/80 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => toggleSection('emails')} data-id="element-159">
            <div className="flex items-center gap-3" data-id="element-160">
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${collapsed.emails ? '-rotate-90' : ''}`} data-id="element-161" />
              <Mail className="w-4 h-4 text-teal-600" data-id="element-162" />
              <span className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-163">
                Emails
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-sm font-medium" data-id="element-164">
              {emails.length} items
            </div>
          </div>

          {!collapsed.emails && <div className="flex flex-col" data-id="element-165">
              {emails.length > 0 ? emails.map(renderSourceRow) : <div className="py-8 text-center text-sm text-gray-500" data-id="element-166">
                  No emails found matching your search.
                </div>}
            </div>}
        </div>

        {/* Documents Section */}
        <div className="flex flex-col" data-id="element-167">
          <div className={`flex items-center justify-between py-4 px-6 bg-gray-50/80 border-y border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors ${collapsed.emails ? 'border-t-0' : ''}`} onClick={() => toggleSection('documents')} data-id="element-168">
            <div className="flex items-center gap-3" data-id="element-169">
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${collapsed.documents ? '-rotate-90' : ''}`} data-id="element-170" />
              <FileText className="w-4 h-4 text-blue-600" data-id="element-171" />
              <span className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-172">
                Documents
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-sm font-medium" data-id="element-173">
              {documents.length} items
            </div>
          </div>

          {!collapsed.documents && <div className="flex flex-col" data-id="element-174">
              {documents.length > 0 ? documents.map(renderSourceRow) : <div className="py-8 text-center text-sm text-gray-500" data-id="element-175">
                  No documents found matching your search.
                </div>}
            </div>}
        </div>
      </div>

      {/* Source Overlay */}
      {selectedSource && <SourceOverlay isOpen={!!selectedSource} onClose={() => setSelectedSource(null)} sourceType={selectedSource.type} sourceTitle={selectedSource.title} sourceDate={selectedSource.date} extractedValue={selectedSource.sampleExtraction} context={selectedSource.sampleContext} data-id="element-176" />}
    </div>;
}